import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  unwrapResult,
} from "@reduxjs/toolkit";
import { ChartGranularity } from "shared/enums/ChartGranularityEnum";
import { ActionSummaryChartFilterConfig } from "interfaces/ActionSummaryChartFilterConfig";
import BaseState from "interfaces/state/BaseState";
import { getSubscriptionConsumptionSummaryByDayAsync } from "apis/DashboardApi";
import { getTokenTagsByProductIdAsync } from "apis/TokenApi";
import { getSubscription } from "store/slices/SubscriptionSlice";
import SubscriptionConsumptionSummaryRequest from "interfaces/request/SubscriptionConsumptionSummaryRequest";
import DailyConsumptionSummary from "interfaces/response/DailyConsumptionSummary";
import { RootState } from "store/store";
import { formattedApiFriendlyDate } from "shared/utility/ApiUtility";
import { getDefaultFromAndToDate } from "shared/utility/ConsumptionSummaryFilterUtility";
import { getProductDetailWithLicenseAndFeatures } from "./ProductSlice";

const getConsumptionSummaryChartDefaultDate = (
  granularity: ChartGranularity
) => {
  const fromDate = formattedApiFriendlyDate(
    getDefaultFromAndToDate(granularity)[0]
  );

  const toDate = formattedApiFriendlyDate(
    getDefaultFromAndToDate(granularity)[1]
  );

  return [fromDate, toDate];
};

interface DashboardState extends BaseState {
  actionSummaryChartFilter: ActionSummaryChartFilterConfig;
  licenseKeyTags: string[];
  consumptionSummariesByDay: DailyConsumptionSummary[];
}

const initialState: DashboardState = {
  loading: false,
  licenseKeyTags: [],
  consumptionSummariesByDay: [],
  actionSummaryChartFilter: {
    selectedTags: [],
    chartViewMode: ChartGranularity.Monthly,
    fromDate: getConsumptionSummaryChartDefaultDate(
      ChartGranularity.Monthly
    )[0],
    toDate: getConsumptionSummaryChartDefaultDate(ChartGranularity.Monthly)[1],
  },
};

export const loadDashboardData = createAsyncThunk<
  any,
  void,
  { state: RootState }
>("product/loadDashboardData", async (_, thunkAPI) => {
  const state = thunkAPI.getState();

  const currentSubscriptionId =
    state.subscriptionData.selectedProductSubscriptionId;

  if (currentSubscriptionId) {
    const dependentDataLoadPromise = thunkAPI
      .dispatch(getSubscription(currentSubscriptionId))
      .then(() => {
        return thunkAPI.dispatch(
          getSubscriptionConsumptionSummaryByDayApplyingFilter()
        );
      });

    const selectedProductId = state.productData.selectedProductId;
    const productDataLoadPromise = thunkAPI.dispatch(
      getProductDetailWithLicenseAndFeatures(selectedProductId)
    );
    const tagDataLoadPromise = thunkAPI.dispatch(
      getProductKeyTags(selectedProductId)
    );

    return Promise.all([
      dependentDataLoadPromise,
      productDataLoadPromise,
      tagDataLoadPromise,
    ]);
  }
});

export const getSubscriptionConsumptionSummaryByDayApplyingFilter =
  createAsyncThunk<any, void, { state: RootState }>(
    "dashboard/getSubscriptionConsumptionSummaryByDayApplyingFilter",
    async (_, thunkAPI) => {
      const state = thunkAPI.getState();
      const filterConfig = state.dashboardData.actionSummaryChartFilter;
      const currentSubscriptionId =
        state.subscriptionData.selectedProductSubscriptionId;

      const request: SubscriptionConsumptionSummaryRequest = {
        subscriptionId: currentSubscriptionId,
        fromDate: filterConfig.fromDate,
        toDate: filterConfig.toDate,
        tags: filterConfig.selectedTags.join(","),
      };

      return thunkAPI
        .dispatch(getSubscriptionConsumptionSummaryByDay(request))
        .then(unwrapResult);
    }
  );

export const getSubscriptionConsumptionSummaryByDay = createAsyncThunk(
  "dashboard/getSubscriptionConsumptionSummaryByDay",
  async (request: SubscriptionConsumptionSummaryRequest) => {
    return await getSubscriptionConsumptionSummaryByDayAsync(request);
  }
);

export const getProductKeyTags = createAsyncThunk(
  "dashboard/getProductKeyTags",
  async (productId: number) => {
    return await getTokenTagsByProductIdAsync(productId);
  }
);

export const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setLoadingStatus: (state, action) => {
      state.loading = action.payload;
    },
    setSelectedTagsFilter: (state, action) => {
      state.actionSummaryChartFilter.selectedTags = action.payload;
    },
    setChartViewModeFilter: (state, action) => {
      state.actionSummaryChartFilter.chartViewMode = action.payload;
      state.actionSummaryChartFilter.fromDate =
        getConsumptionSummaryChartDefaultDate(action.payload)[0];
      state.actionSummaryChartFilter.toDate =
        getConsumptionSummaryChartDefaultDate(action.payload)[1];
    },
    setFromAndToDateFilter: (
      state,
      action: PayloadAction<{ fromDate: string; toDate: string }>
    ) => {
      state.actionSummaryChartFilter.fromDate = action.payload.fromDate;
      state.actionSummaryChartFilter.toDate = action.payload.toDate;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProductKeyTags.pending, (state) => {
      state.loading = true;
      state.licenseKeyTags = [];
    });
    builder.addCase(getProductKeyTags.fulfilled, (state, action) => {
      state.loading = false;
      state.licenseKeyTags = action.payload;
    });
    builder.addCase(getProductKeyTags.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getSubscriptionConsumptionSummaryByDay.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getSubscriptionConsumptionSummaryByDay.fulfilled,
      (state, action) => {
        state.loading = false;
        state.consumptionSummariesByDay = action.payload;
      }
    );
    builder.addCase(
      getSubscriptionConsumptionSummaryByDay.rejected,
      (state) => {
        state.loading = false;
      }
    );
  },
});

export const {
  setSelectedTagsFilter,
  setChartViewModeFilter,
  setFromAndToDateFilter,
  setLoadingStatus,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
